// @ts-nocheck
import React, { useState, useEffect } from 'react';
import Button from '@/components/button';
import Breadcrumbs from '@/components/breadcrumbs';
import Search from '@/components/search';
import Table from '@/components/table';
import { colors, fontSizes, layout, fontFamily, shadows } from '@/theme';
import CellDate from '@/components/cells/date';
import Pagination from '@/components/pagination';
import KendoPager from '@/components/kendo/kendo-pager';
import Fetch from '@/utils/Fetch';
import Router from 'next/router';
import Storage from '@/utils/storage';
import * as Enums from '@/utils/enums';
import Helper from '@/utils/helper';
import KendoCellDate from '@/components/kendo/cells/kendo-cell-date';
import KendoTable from '@/components/kendo/kendo-table';
import { createFileRoute } from '@tanstack/react-router'



const COLUMNS = [{
  Label: 'Name',
  ColumnName: 'Description',
  CellType: 'none',
}, {
  Label: 'Account Code',
  ColumnName: 'Code',
  CellType: 'none',
}, {
  Label: 'Updated By',
  ColumnName: 'ModifiedBy',
  CellType: 'none',
}, {
  Label: 'Updated Date',
  ColumnName: 'ModifiedDate',
  CellType: 'date',
}];

const INCLUDE_DISABLED_FILTER = [{
  type: Enums.ControlType.Switch,
  label: 'Include disabled categories',
}];

function InventoryCategoryList() {

  const [columnState, setColumnState] = useState(COLUMNS);
  const [inventoryCategoryResults, setInventoryCategoryResults] = useState([{}]);

  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchVal, setSearchVal] = useState('');
  const [ancillaryFilters, setAncillaryFilters] = useState({ IncludeDisabled: false });
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);

  const getAccessStatus = () => {
    let subscriptionInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo);
    if (subscriptionInfo) {
      setAccessStatus(subscriptionInfo.AccessStatus);
    }
  };

  const handleAncillaryFilterChange = (result: any) => {
    if (result.reset) {
      setAncillaryFilters({ IncludeDisabled: false });
    } else {
      if (result.key == "IncludeDisabled") {
        setAncillaryFilters({ IncludeDisabled: result.checked });
      }
    }
  };

  const [pageSize, setPageSize] = useState(10);

  const pageSizeChanged = (size: any) => {
    setPageSize(size);
  };

  const pageChanged = (page: any) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    getAccessStatus();
  }, []);

  useEffect(() => {
    if (accessStatus === Enums.AccessStatus.LockedWithOutAccess) {
      Helper.nextRouter(Router.replace, "/");
    }
  }, [accessStatus]);

  useEffect(() => {
    if (currentPage == 1) {
      searchInventoryCategories();
    } else {
      setCurrentPage(1);
    }
  }, [sortField, sortDirection, ancillaryFilters, pageSize]);

  useEffect(() => {
    searchInventoryCategories();
  }, [currentPage]);

  async function searchInventoryCategories() {
    setSearching(true);

    const inventoryCategories = await Fetch.post({
      url: `/InventoryCategory/GetInventoryCategories`,
      params: {
        pageSize: pageSize,
        pageIndex: (currentPage - 1),
        searchPhrase: searchVal,
        IncludeClosed: ancillaryFilters["IncludeDisabled"],
        SortExpression: sortField,
        SortDirection: sortDirection,
      }
    });
    setInventoryCategoryResults(inventoryCategories.Results);
    setTotalResults(inventoryCategories.TotalResults);

    setSearching(false);
  }

  const columns = React.useMemo(
    () => columnState.map(function (column: any) {
      let columnObject = {
        Header: column.Label,
        accessor: column.ColumnName,
        ColumnName: column.ColumnName,
        UserWidth: column.UserWidth
      }

      if (column.CellType != "none") {
        switch (column.CellType) {
          case 'date':
            columnObject['Cell'] = ({ cell: { value } }: any) => <CellDate value={value} />;
            columnObject['KendoCell'] = (props: any) => <KendoCellDate {...props} />;
            break;
        }
      }

      return columnObject;
    }),
    [columnState]
  );

  const setSort = (field: any) => {
    setSortDirection(Helper.getSortDirection(field, sortField, sortDirection));
    setSortField(field);
  };

  function rowClick(row: any) {
    Helper.nextRouter(Router.push, '/inventory-category/[id]', `/inventory-category/${row.original.ID}`);
  }

  const createCategory = () => {
    Helper.nextRouter(Router.push, "/inventory-category/create");
  }

  // for table resize columns
  async function onColumnResize(columnName: any, width: any, columnNamesAndWidths: any) {
    const newState = columnState.map(function (column: any) {
      if (column.ColumnName === columnName) {
        column.UserWidth = width;
      } else if (columnNamesAndWidths) {
        let match = columnNamesAndWidths.find((x: any) => x.columnName === column.ColumnName);
        if (match) {
          column.UserWidth = match.width;
        }
      }
      return column;
    });
    setColumnState(newState);
  }
  // for table resize columns

  return (
    <div className="list-container">
      <div className="row">
        <div className="title">
          <Breadcrumbs currPage={{ text: 'Inventory Category List', link: '/inventory-category/list', type: 'list' }} />
        </div>
        <Button disabled={accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}
          text="Create" icon="plus-circle" extraClasses="fit-content no-margin" onClick={createCategory} />
      </div>

      <div className="row end">
        <div className="search-container">
          <Search
            placeholder="Search inventory category name"
            resultsNum={inventoryCategoryResults.length}
            searchVal={searchVal}
            setSearchVal={setSearchVal}
            ancillaryFilters={INCLUDE_DISABLED_FILTER}
            setAncillaryFilters={handleAncillaryFilterChange}
            searchFunc={searchInventoryCategories}
          />
        </div>
      </div>

      <div className={"no-items" + (inventoryCategoryResults.length == 0 ? " no-items-visible" : "")}>
        <div className={"loading-overlay" + (searching ? " loading-overlay-visible" : "")}>
          <div className="loader"></div>
        </div>
        <img src="/job-folder.svg" alt="Folder" />
        <h3>No Inventory Category found</h3>
        <p>If you can't find a Inventory Category, try another search or create a new one.</p>
        <a href="/inventory-category/create"><img src="/icons/plus-circle-blue.svg" alt="plus" /> Add new Inventory Category</a>
        <img className="wave" src="/wave.svg" alt="wave" />
      </div>

      {/* <div className={"table-container" + (inventoryCategoryResults.length != 0 ? " table-container-visible" : "")}>
        <div className={"loading-overlay" + (searching ? " loading-overlay-visible" : "")}>
          <div className="loader"></div>
        </div>
        <Table actions={[
            {text: "Edit", icon: "edit", function: (row) => Helper.nextRouter(Router.push,'/inventory-category/[id]', `/inventory-category/${row.ID}`)},
          ]}
          columns={columns} data={inventoryCategoryResults} setSort={setSort} sortField={sortField} sortDirection={sortDirection} rowClick={rowClick} />
      </div> */}

      {inventoryCategoryResults.length != 0 ? <KendoTable
        searching={searching}
        actions={[
          { text: "Edit", icon: "edit", function: (row: any) => Helper.nextRouter(Router.push, '/inventory-category/[id]', `/inventory-category/${row.ID}`) },
        ]}
        columns={columns}
        data={inventoryCategoryResults}
        rowClick={rowClick}
        setSort={setSort}
        sortField={sortField}
        sortDirection={sortDirection}
        type="InventoryCategory"
        // for table resize columns
        onColumnResize={onColumnResize}
        // for table resize columns
        canSelectItems={false}
        heightOffset={280}
        highlightColumnName="Description"
      /> : ""}

      <KendoPager pageSizeChanged={pageSizeChanged} pageChanged={pageChanged} totalResults={totalResults} searchValue={searchVal} parentPageNumber={currentPage} />

      <style jsx>{`
        .column {
          width: 100%;
        }
        .column-margin {
          margin-left: 24px;
        }
        .button-container {
          flex-shrink: 0;
          width: 12rem;
        }
        .button-container :global(.button){
          margin-top: 0.5rem;
        }
        .row {
          display: flex;
          justify-content: space-between;
        }
        .end {
          align-items: flex-end;
        }
        .row.top-gap {
          margin-top: 2.5rem;
        }
        .search-container :global(.search) {
          width: 528px;
        }
        a {
          text-decoration: none;
        }
      `}</style>
    </div>
  );
}

export default InventoryCategoryList

export const Route = createFileRoute('/_authenticated/inventory-category/list')({ component: InventoryCategoryList })
